/**
 * `/events/:id`.
 *
 * ## The one thing still missing here, and why the placard stayed
 *
 * `contacts.list.metAtEventId` remains a 422 — re-verified 2026-08-29 against
 * `apps/api/src/orpc/router.ts`, whose `contacts.list` handler still passes
 * `metAtEventId` to `rejectUnsupported`. So there is no way to list the people
 * linked to this event, and rendering an arbitrary page of contacts under a
 * heading that claims they were met here would be a fabricated fact on the one
 * screen whose entire job is provenance.
 *
 * It is therefore a `Planned` placeholder — bucket (c) of the 2026-08-29 sweep —
 * with copy rewritten for the person reading it rather than for the person who
 * will build it. The count IS shown, because `events.get` returns it and it is
 * true.
 */

import { Badge, EmptyState, SkeletonRows } from '@crm/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useEventQuery } from '../../../api/queries.ts';
import { PageBody, PageHeader } from '../../../components/page.tsx';
import { Planned } from '../../../components/placeholders.tsx';
import { RouteErrorPanel } from '../../../components/route-error-boundary.tsx';
import { formatDateTime } from '../../../lib/format.ts';

export const Route = createFileRoute('/_authed/events/$eventId')({
  component: EventDetailPage,
  errorComponent: RouteErrorPanel,
});

function EventDetailPage() {
  const { eventId } = Route.useParams();
  const event = useEventQuery(eventId);

  if (event.isLoading) {
    return <SkeletonRows rows={5} className="p-4" />;
  }
  if (event.isError || event.data === undefined) {
    return <EmptyState kind="error" className="m-4" title="Could not load this event" />;
  }

  const detail = event.data;

  return (
    <>
      <PageHeader
        title={detail.name}
        description={
          [
            formatDateTime(detail.startsAt),
            detail.locationText,
            [detail.city, detail.country].filter(Boolean).join(', ') || null,
          ]
            .filter((part): part is string => part !== null && part !== '')
            .join(' · ') || 'No date or location recorded'
        }
        actions={<Badge variant="muted">{detail.kind.replace('_', ' ')}</Badge>}
      />
      <PageBody>
        {detail.notes === null ? null : (
          <p className="whitespace-pre-wrap break-words text-sm text-muted-foreground">
            {detail.notes}
          </p>
        )}

        <Planned
          title="People you met here"
          description={`${detail.contactCount} ${
            detail.contactCount === 1 ? 'person is' : 'people are'
          } recorded as met at this event. Listing them here is coming; for now their contact pages each show this event under “Met at”.`}
        />
      </PageBody>
    </>
  );
}
